/**
 * SnsRoleSelectPage — A-3-5 / A-4-5. SNS 가입 역할 선택
 * 진입: AuthCallbackPage → SOCIAL_NEED_REGISTER → /signup/sns (route state)
 * 분기: 사장님 → /signup/sns/master / 소속 직원 → /signup/sns/staff
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Badge, Button, IconCheckCircle } from '@indipia/ui-core';
import { AuthLayout } from '../../app/layouts/AuthLayout';
import { RoleSelectCards, type SignupRole } from '../components/RoleSelectCards';

// route state 타입 — AuthCallbackPage에서 전달
interface SnsRouteState {
  provider:     string;
  socialId:     string;
  email:        string | null;
  socialName:   string | null;
  snsLinkToken: string;
}

const PROVIDER_LABEL: Record<string, string> = {
  kakao:  '카카오',
  naver:  '네이버',
  google: '구글',
};

export function SnsRoleSelectPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const sns       = (location.state ?? {}) as Partial<SnsRouteState>;

  const [role, setRole] = useState<SignupRole>('master');

  // provider 를 못 받은 경우 "SNS SNS 인증 완료" 가 되지 않게 접두사를 비운다.
  const providerLabel = PROVIDER_LABEL[sns.provider ?? ''];

  const handleNext = () => {
    const next = role === 'master' ? '/signup/sns/master' : '/signup/sns/staff';
    void navigate(next, { state: sns });
  };

  return (
    <AuthLayout
      kicker="회원가입"
      title="가입 유형을 선택해 주세요"
      subtitle={<>인증된 정보를 확인하고<br />가입 유형을 선택해 주세요.</>}
      align="center"
      banner={
        <div className="flex justify-center">
          {/* 아이콘 동반 — 색만으로 의미를 전달하지 않는다 (D-07 §19.3) */}
          <Badge status="success">
            <IconCheckCircle width={13} height={13} />
            {providerLabel ? `${providerLabel} SNS 인증 완료` : 'SNS 인증 완료'}
          </Badge>
        </div>
      }
      actions={
        <div className="flex gap-sm">
          {/* 보조 강조 액션은 secondary(아웃라인) — D-09 §15.1 Variants */}
          <Button variant="secondary" size="lg" className="shrink-0" onClick={() => void navigate('/login')}>
            ← 이전
          </Button>
          <Button variant="primary" size="lg" className="flex-1" onClick={handleNext}>
            다음 →
          </Button>
        </div>
      }
    >
      {/* SNS 정보 카드 (readonly) */}
      <dl
        className="mb-06 px-05 py-05"
        style={{
          background: 'var(--shell-bg)',
          border: '1px solid var(--shell-border)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <div className="flex items-center gap-sm">
          <dt className="w-09 shrink-0 text-caption font-semibold text-text-secondary">이름</dt>
          <dd className="text-body-sm font-semibold text-text">{sns.socialName ?? '—'}</dd>
        </div>
        <div className="mt-03 flex items-center gap-sm border-t border-border pt-03">
          <dt className="w-09 shrink-0 text-caption font-semibold text-text-secondary">이메일</dt>
          <dd className="text-body-sm font-semibold text-text">{sns.email ?? '—'}</dd>
        </div>
      </dl>

      <RoleSelectCards value={role} onChange={setRole} />
    </AuthLayout>
  );
}
